/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import {
    autoUpdate,
    computePosition,
    flip,
    offset,
    shift,
} from '@floating-ui/dom';


/*
 * Tooltips for glossary terms.
 *
 * Replaces jQuery UI's tooltip widget, which brought jQuery and jQuery UI
 * along for one behaviour. Floating UI does the positioning and nothing
 * else, which is what makes the bubble stylable from the map's own design
 * settings: there is no theme to fight with, only the properties set here.
 *
 * Three things the widget gave us and that are kept:
 *
 * - **Edge avoidance.** `flip` moves the bubble to the other side of the
 *   term when it would leave the viewport, `shift` slides it along the edge
 *   rather than letting it hang off. This is jQuery UI's `collision:
 *   flipfit`.
 * - **Keyboard and pointer parity.** A term is focusable, so the definition
 *   must open on focus as well as hover, and Escape must close it.
 * - **Touch.** A tap opens the bubble and a tap elsewhere closes it. Hover
 *   alone leaves a touch reader with no way in at all.
 */

const BUBBLE_CLASS = 'glossary-tooltip';
const GAP = 5;

let counter = 0;

/**
 * Create a tooltip controller.
 *
 * One bubble element is shared by every term: only one can be open, and
 * reusing it keeps the document from growing a node per glossary word.
 *
 * @param {object} [options]
 * @param {object} [options.style] - CSS properties for the bubble, from the
 *   map's design settings.
 * @param {number} [options.gap] - Distance from the term, in pixels.
 * @returns {object} A controller with `attach`, `setStyle`, `hide` and
 *   `destroy`.
 */
export function createTooltip(options = {}) {
    let style = options.style || {};
    const gap = options.gap ?? GAP;

    let bubble = null;
    let stopAutoUpdate = null;
    let openFor = null;
    // Escape leaves focus on the term, and a focused term opens its
    // tooltip -- so without this the dismissal is undone immediately.
    let dismissed = null;
    const attached = [];

    function ensureBubble() {
        if (bubble && bubble.isConnected) {
            return bubble;
        }
        bubble = document.createElement('div');
        bubble.className = BUBBLE_CLASS;
        bubble.setAttribute('role', 'tooltip');
        bubble.id = `glossary-tooltip-${(counter += 1)}`;
        bubble.hidden = true;
        // `visibility`, not `opacity` alone: an element faded out with
        // opacity stays in the tab order and the accessibility tree.
        bubble.style.position = 'absolute';
        bubble.style.top = '0';
        bubble.style.left = '0';
        document.body.appendChild(bubble);
        applyStyle();
        return bubble;
    }

    function applyStyle() {
        if (!bubble) return;
        Object.entries(style).forEach(([property, value]) => {
            bubble.style.setProperty(
                property.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`),
                value
            );
        });
    }

    async function position(element) {
        if (!bubble) return;
        const target = bubble;
        const result = await computePosition(element, target, {
            placement: 'bottom-start',
            middleware: [offset(gap), flip(), shift({ padding: gap })],
        });
        // Positioning is asynchronous, so the bubble may have been closed
        // or destroyed while it was being computed.
        if (target !== bubble || openFor !== element) return;
        bubble.style.left = `${result.x}px`;
        bubble.style.top = `${result.y}px`;
    }

    function show(element, content) {
        if (!content) return;
        ensureBubble();
        hide();
        bubble.textContent = content;
        bubble.hidden = false;
        openFor = element;
        element.setAttribute('aria-describedby', bubble.id);
        position(element);
        // Keep it anchored while the map pans, zooms or the page scrolls.
        stopAutoUpdate = autoUpdate(element, bubble, () => position(element));
    }

    function hide() {
        stopAutoUpdate?.();
        stopAutoUpdate = null;
        if (openFor) {
            openFor.removeAttribute('aria-describedby');
            openFor = null;
        }
        if (bubble) {
            bubble.hidden = true;
        }
    }

    function onDocumentPointerDown(event) {
        if (openFor && !openFor.contains(event.target)) {
            hide();
        }
    }

    function onKeyDown(event) {
        if (event.key === 'Escape' && openFor) {
            // Focus stays on the term, which is where it already was: the
            // reader needs somewhere to be, and the term is the thing they
            // were reading about.
            dismissed = openFor;
            hide();
        }
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onDocumentPointerDown, true);

    return {
        /**
         * Give an element a tooltip.
         *
         * @param {HTMLElement} element - The term.
         * @param {string} content - The definition.
         */
        attach(element, content) {
            if (!element || !content) return;

            const open = () => {
                if (dismissed === element) return;
                show(element, content);
            };
            const close = () => {
                // Leaving the term clears the dismissal, so coming back
                // opens it again.
                if (dismissed === element) dismissed = null;
                if (openFor === element) hide();
            };
            const toggle = (event) => {
                event.preventDefault();
                if (openFor === element) {
                    hide();
                } else {
                    dismissed = null;
                    show(element, content);
                }
            };

            element.addEventListener('mouseenter', open);
            element.addEventListener('mouseleave', close);
            element.addEventListener('focus', open);
            element.addEventListener('blur', close);
            // A touch reader has no hover, so a tap has to do the work.
            element.addEventListener('touchstart', toggle, { passive: false });

            attached.push({ element, open, close, toggle });
        },

        /**
         * Restyle the bubble from the map's design settings.
         *
         * @param {object} next - CSS properties.
         */
        setStyle(next) {
            style = next || {};
            applyStyle();
        },

        /** The element currently described, if any. */
        get openElement() {
            return openFor;
        },

        /** The bubble element, once one exists. */
        get element() {
            return bubble;
        },

        hide,

        /** Detach every listener and remove the bubble. */
        destroy() {
            hide();
            attached.forEach(({ element, open, close, toggle }) => {
                element.removeEventListener('mouseenter', open);
                element.removeEventListener('mouseleave', close);
                element.removeEventListener('focus', open);
                element.removeEventListener('blur', close);
                element.removeEventListener('touchstart', toggle);
            });
            attached.length = 0;
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener(
                'pointerdown',
                onDocumentPointerDown,
                true
            );
            bubble?.remove();
            bubble = null;
        },
    };
}

export default createTooltip;
